import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CardMiniature from '../components/evangile/CardMiniature';
import { useEvangile, EvangileItem } from '../hooks/useEvangile';

const formatDate = (dateValue: string, locale: string, withYear: boolean) => {
  if (!dateValue) return '';
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;

  const options: Intl.DateTimeFormatOptions = withYear
    ? { day: '2-digit', month: 'long', year: 'numeric' }
    : { day: '2-digit', month: 'short' };

  return new Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Les langues dans lesquelles le commentaire du jour paraît réellement.
 *
 * Le site parle sept langues, la publication quotidienne deux : les
 * collections Firestore `evangile_es`, `_it`, `_de`, `_ru` et `_el` sont vides
 * — relevé le 12/08/2026. À compléter le jour où le pipeline en publie
 * d'autres.
 */
const AVAILABLE_LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

const EvangileduJour: React.FC = () => {
  const { lang } = useParams();
  const locale = lang || 'fr';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');

  React.useEffect(() => {
    if (dateParam) {
      navigate(`/${locale}/evangile/${dateParam}`, { replace: true });
    }
  }, [dateParam, locale, navigate]);


  const { items, loading, loadingMore, error, hasMore, loadMore } = useEvangile(locale);

  const todayKey = new Date().toLocaleDateString('fr-CA');
  const filteredItems = items.filter(i => i.date <= todayKey);
  const today = filteredItems[0]?.date === todayKey ? filteredItems[0] : null;
  const previous = today ? filteredItems.slice(1) : filteredItems;

  const labels = useMemo(() => {
    const translate = (key: string) => String(t(key));

    return {
      title: translate('evangile.titre'),
      subtitle: translate('evangile.soustitre'),
      today: translate('evangile.aujourdhui'),
      previous: translate('evangile.precedents'),
      loadMore: translate('evangile.charger'),
      read: translate('evangile.lire'),
      open: translate('evangile.ouvrir'),
      passage: translate('evangile.passage'),
      bientot: translate('evangile.bientot'),
      disponibleEn: translate('evangile.disponibleEn'),
      share: translate('evangile.partager'),
      nativeShare: translate('evangile.nativeShare'),
      ctaButton: translate('evangile.cta.button'),
    };
  }, [t]);

  const openDetail = (item: EvangileItem) => {
    navigate(`/${locale}/evangile/${item.date}`);
  };

  // Détermine l'URL canonique pour la liste :
  const canonicalUrl = `https://bible4.me/${locale}/evangile`;

  if (dateParam) return null;

  return (
    <>
      <Helmet>
        <title>{labels.title} — Bible4Me</title>
        <meta name="description" content={t('seo.evangile.description')} />
        <meta property="og:title" content={`${labels.title} — Bible4Me`} />
        <meta property="og:description" content={t('seo.evangile.description')} />
        <meta property="og:image" content="https://bible4.me/logo%20626x626.png" />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="min-h-screen bg-amber-100 text-amber-900">
        <div className="sticky top-0 z-20 bg-amber-200 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(`/${locale}`)}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
            >
              <img src="/logo%20626x626.png" alt="Bible4Me" className="w-6 h-6 rounded-md object-contain" />
              <span className="font-bold text-amber-900 text-sm">Bible4Me</span>
            </button>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(`/${locale}/daily-readings`)}
                className="text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors underline underline-offset-2"
              >
                {/* Le second argument n'est qu'un filet : la clé existe
                    désormais dans les 7 locales. Elle manquait partout, si
                    bien que ce défaut français s'affichait en italien comme
                    en grec. */}
                {t('home.nav.dailyReadings', 'Lectures du jour')}
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 pt-4 pb-5 bg-amber-800">
          <h1 className="text-white font-serif text-xl font-normal mb-1">{labels.title}</h1>
          <p className="text-amber-200 text-xs">{labels.subtitle}</p>
        </div>
        <div className="h-3 bg-amber-800 rounded-b-3xl" />

        <div className="px-4 py-4 pb-24">
          {loading && (
            <div className="text-center text-sm text-amber-700">Chargement...</div>
          )}
          {!loading && Boolean(error) && (
            <div className="text-center text-sm text-amber-700">Erreur de chargement.</div>
          )}

          {/* Seules les collections `evangile_fr` et `evangile_en` sont
              alimentées — vérifié le 12/08/2026, les cinq autres sont vides.
              Dans ces langues la liste revenait vide et la page n'affichait
              qu'une bande jaune, sans un mot d'explication. */}
          {!loading && !error && !today && previous.length === 0 && (
            <div className="text-center py-10 px-4">
              <div className="text-3xl mb-3" aria-hidden="true">🕊️</div>
              <p className="text-sm text-amber-800 mb-5">{labels.bientot}</p>
              <p className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-3">
                {labels.disponibleEn}
              </p>
              <div className="flex justify-center gap-3">
                {/* Les noms de langue restent dans leur propre langue : c'est
                    ce que le lecteur reconnaît, quelle que soit la sienne. */}
                {AVAILABLE_LANGUAGES.filter(l => l.code !== locale).map(l => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => navigate(`/${l.code}/evangile`)}
                    className="bg-amber-800 text-amber-50 rounded-xl py-2 px-5 text-sm font-semibold hover:bg-amber-900 transition-colors"
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && today && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-600 shadow-[0_0_0_3px_rgba(217,119,6,0.2)]" />
                <span className="text-[0.62rem] uppercase tracking-wider font-bold text-amber-800">
                  {labels.today}
                </span>
              </div>
              <CardMiniature
                evangile={today}
                isToday
                onClick={() => openDetail(today)}
                dateLabel={formatDate(today.date, locale, false)}
                readLabel={labels.read}
              />
            </div>
          )}

          {!loading && previous.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-1.5 w-1.5 rounded-full border-2 border-amber-600" />
                <span className="text-[0.62rem] uppercase tracking-wider font-bold text-amber-800">
                  {labels.previous}
                </span>
              </div>
              <div className="space-y-3">
                {previous.map(item => (
                  <div key={item.id}>
                    <CardMiniature
                      evangile={item}
                      isToday={false}
                      onClick={() => openDetail(item)}
                      dateLabel={formatDate(item.date, locale, false)}
                      readLabel={labels.read}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination SEO-friendly (liens pour les bots, discret pour l'utilisateur) */}
          {!loading && (
            <nav aria-label="Pagination évangile" style={{ fontSize: '0.01px', height: 0, overflow: 'hidden', pointerEvents: 'none' }}>
              {/* Supposons 20 items par page, calcule le nombre de pages */}
              {(() => {
                const itemsPerPage = 20;
                const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
                const currentPage = 1; // Toujours page 1 côté JS scroll infini
                if (totalPages <= 1) return null;
                return (
                  <ul style={{ display: 'flex', gap: 4 }}>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <li key={i}>
                        <a
                          href={`/${locale}/evangile${i === 0 ? '' : `?page=${i + 1}`}`}
                          rel={i === currentPage - 1 ? 'canonical' : 'next'}
                        >
                          Page {i + 1}
                        </a>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </nav>
          )}
          {/* Bouton scroll infini pour l'utilisateur */}
          {!loading && hasMore && (
            <div className="text-center mt-5">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="text-amber-600 font-semibold text-sm inline-flex flex-col items-center gap-1 disabled:opacity-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                {labels.loadMore}
              </button>
            </div>
          )}
        </div>

        {/* Plus de DetailSheet ici, la navigation se fait vers /:lang/evangile/:date */}

        {/* CTA sticky download + WhatsApp share */}
        <div className="fixed bottom-0 inset-x-0 z-30 bg-amber-900 px-4 py-3 shadow-lg">
          <p className="text-center text-amber-200 text-xs font-semibold mb-2 uppercase tracking-wider">
            {labels.ctaButton}
          </p>
          <div className="flex gap-3">
            <a
              href="https://apps.apple.com/fr/app/bible4me/id6743235720"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-black text-white rounded-xl py-2 px-3 flex items-center justify-center gap-2 hover:bg-gray-900 transition-colors"
            >
              {/* Apple logo */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.38.07 2.34.74 3.16.8 1.2-.24 2.35-.93 3.63-.84 1.54.12 2.7.72 3.46 1.83-3.17 1.9-2.42 6.05.7 7.22-.62 1.5-1.44 2.99-2.95 3.87zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              <div className="leading-tight">
                <div className="text-[0.6rem] text-gray-300">App Store</div>
                <div className="text-sm font-bold">iPhone / iPad</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.bible4md.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-black text-white rounded-xl py-2 px-3 flex items-center justify-center gap-2 hover:bg-gray-900 transition-colors"
            >
              {/* Google Play logo */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3.18 1.47A1.5 1.5 0 001.5 3v18a1.5 1.5 0 001.68 1.53L14.25 12 3.18 1.47z" fill="#4FC3F7"/>
                <path d="M17.96 9.3L14.25 12l3.71 2.7 3.35-1.93a1.5 1.5 0 000-2.6L17.96 9.3z" fill="#FFD54F"/>
                <path d="M14.25 12L3.18 22.53c.17.16.38.28.62.33l12.5-7.16L14.25 12z" fill="#F06292"/>
                <path d="M14.25 12L3.8 1.14c-.24.05-.45.17-.62.33L14.25 12z" fill="#81C784"/>
              </svg>
              <div className="leading-tight">
                <div className="text-[0.6rem] text-gray-300">Google Play</div>
                <div className="text-sm font-bold">Android</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default EvangileduJour;